/**
 * Optimizer Control Center — every Flow Optimizer tracker's configs, merged
 * For Media Devoted | Created By Ciro Autiero
 *
 * Partial-failure tolerant: one tracker erroring still surfaces the others'
 * rows plus the failed tracker for an inline warning. One query per tracker
 * (useFlowOptimizerTrackers, registry order: Voluum, Binom, then the rest).
 */

import { useCallback } from 'react';
import { useQueries, type UseQueryResult } from '@tanstack/react-query';
import type { FlowOptimizerConfigListItem, TrackerId } from '@trafficpulse/shared';

import {
  listConfigs,
  type FlowOptimizerProvider,
} from '@/features/flow-optimizer/services/flow-optimizer-api';
import type { OptimizerRow } from '@/features/optimizer-center/optimizer-center.types';
import { useFlowOptimizerTrackers } from '@/features/trackers/flow-optimizer/useFlowOptimizerTrackers';

export const optimizerCenterKeys = {
  all: ['optimizer-center'] as const,
  configs: (provider: FlowOptimizerProvider) =>
    ['optimizer-center', 'configs', provider] as const,
  scheduler: (provider: FlowOptimizerProvider) =>
    ['optimizer-center', 'scheduler', provider] as const,
};

export interface UseOptimizerConfigsResult {
  rows: OptimizerRow[];
  /** True only while no tracker has answered yet. */
  isLoading: boolean;
  isFetching: boolean;
  failedProviders: FlowOptimizerProvider[];
  /** Trackers whose first load is still in flight (Voluum is the slow one). */
  loadingProviders: FlowOptimizerProvider[];
  refetch: () => void;
}

type ConfigsQuery = UseQueryResult<FlowOptimizerConfigListItem[]>;

/** Rows tagged with their tracker, in tracker order, plus the per-tracker states. */
export function summarizeConfigs(
  trackers: readonly TrackerId[],
  results: ConfigsQuery[],
): UseOptimizerConfigsResult {
  const where = (test: (r: ConfigsQuery) => boolean) =>
    trackers.filter((_, i) => results[i] !== undefined && test(results[i]));
  return {
    rows: trackers.flatMap((provider, i) =>
      (results[i]?.data ?? []).map((r) => ({ ...r, provider })),
    ),
    isLoading: results.every((r) => r.isLoading),
    isFetching: results.some((r) => r.isFetching),
    failedProviders: where((r) => r.isError),
    loadingProviders: where((r) => r.isLoading),
    refetch: () => results.forEach((r) => void r.refetch()),
  };
}

export function useOptimizerConfigs(): UseOptimizerConfigsResult {
  const trackers = useFlowOptimizerTrackers();
  const combine = useCallback(
    (results: ConfigsQuery[]) => summarizeConfigs(trackers, results),
    [trackers],
  );
  return useQueries({
    queries: trackers.map((provider) => ({
      queryKey: optimizerCenterKeys.configs(provider),
      queryFn: () => listConfigs(provider),
      refetchInterval: 30_000,
    })),
    combine,
  });
}
